import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Layout from "../components/Layout";
import apiFetch from "../utils/apiFetch";
import {
  DASH,
  collectRecordFiles,
  displayValue,
  formatDate,
  getFileId,
  sortByDateDesc,
} from "../utils/equipmentPassportConfig";

const DIRECTUS_URL =
  process.env.REACT_APP_DIRECTUS_URL || "https://map.uzbekistonmet.uz";

function PassportState({ title, text, tone = "muted" }) {
  return (
    <Layout>
      <div className="container">
        <div className={`passport-state passport-state--${tone}`}>
          <h1>{title}</h1>
          {text && <p>{text}</p>}
        </div>
      </div>
    </Layout>
  );
}

function FieldGrid({ fields }) {
  return (
    <dl className="passport-field-grid">
      {fields.map((field) => (
        <div className="passport-field" key={field.key}>
          <dt>{field.label}</dt>
          <dd>{displayValue(field.value)}</dd>
        </div>
      ))}
    </dl>
  );
}

function InspectionTimeline({ inspections }) {
  if (!inspections.length) {
    return <div className="passport-empty">Кўриклар бўйича маълумот йўқ.</div>;
  }

  return (
    <div className="passport-timeline">
      {inspections.map((inspection, index) => {
        const inspector = inspection.inspectorUser
          ? `${inspection.inspectorUser.lastname || ""} ${inspection.inspectorUser.name || ""}`.trim()
          : null;

        return (
          <article className="passport-timeline__item" key={inspection.id || index}>
            <div className="passport-timeline__rail" />
            <div className="passport-timeline__body">
              <div className="passport-timeline__top">
                <span className="passport-chip passport-chip--maintenance">
                  {formatDate(inspection.date || inspection.date_created)}
                </span>
                {inspector && <span className="passport-timeline__author">{inspector}</span>}
              </div>
              <h3>{inspection.comment ? "Кўрик натижаси" : "Кўрик ёзуви"}</h3>
              <p>{inspection.comment || "Изоҳ киритилмаган."}</p>
            </div>
          </article>
        );
      })}
    </div>
  );
}

function FileGrid({ files }) {
  const safeFiles = (Array.isArray(files) ? files : [])
    .map((file) => ({ ...file, fileId: getFileId(file) }))
    .filter((file) => file.fileId);

  if (!safeFiles.length) {
    return <div className="passport-empty">Фото ёки файллар бириктирилмаган.</div>;
  }

  return (
    <div className="passport-file-grid">
      {safeFiles.map((file, index) => (
        <a
          className="passport-file-card"
          href={`${DIRECTUS_URL}/assets/${file.fileId}`}
          target="_blank"
          rel="noreferrer"
          key={`${file.fileId}-${index}`}
        >
          <span className="passport-file-card__mark">FILE</span>
          <span>{file.record_topic || `Файл ${index + 1}`}</span>
          <small>{formatDate(file.record_date)}</small>
        </a>
      ))}
    </div>
  );
}

const Poles = () => {
  const { slug, poleId } = useParams();
  const [network, setNetwork] = useState(null);
  const [pole, setPole] = useState(null);
  const [city, setCity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setNotFound(false);
      setError(null);

      try {
        const poleRes = await apiFetch(`/api/poles/${poleId}`, { cache: "no-store" });

        if (poleRes.status === 404) {
          if (!cancelled) setNotFound(true);
          return;
        }

        if (!poleRes.ok) {
          const text = await poleRes.text();
          throw new Error(text || "Не удалось загрузить опору");
        }

        const poleData = await poleRes.json();
        const currentNetwork = poleData?.network || null;
        const currentCity = poleData?.city || null;

        if (!currentNetwork || String(currentNetwork.slug) !== String(slug)) {
          if (!cancelled) setNotFound(true);
          return;
        }

        if (!cancelled) {
          setPole(poleData);
          setNetwork(currentNetwork);
          setCity(currentCity);
        }
      } catch (e) {
        if (!cancelled) setError(e.message || "Ошибка загрузки");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    if (poleId) load();
    return () => {
      cancelled = true;
    };
  }, [slug, poleId]);

  const view = useMemo(() => {
    if (!pole || !network) return null;
    const inspections = sortByDateDesc(pole.inspections);
    const files = [
      ...(Array.isArray(pole.files) ? pole.files : []),
      ...collectRecordFiles(inspections),
    ];
    const latestInspection = inspections[0] || null;

    return {
      inspections,
      files,
      latestInspection,
      fields: [
        { key: "name", label: "Таянч номери", value: pole.name },
        { key: "network", label: "Линия", value: network.name },
        { key: "city", label: "Филиал", value: city?.name },
        { key: "voltage", label: "Кучланиш", value: network.voltage ? `${network.voltage} кВ` : null },
        { key: "latitude", label: "Кенглик", value: pole.latitude },
        { key: "longitude", label: "Узунлик", value: pole.longitude },
        { key: "pole_brand", label: "Таянч маркаси", value: pole.pole_brand },
        { key: "pole_type", label: "Таянч тури", value: pole.pole_type },
        { key: "wire_type", label: "Сим тури", value: pole.wire_type },
        { key: "lightning_wire_type", label: "Чақмоқдан ҳимоя троси", value: pole.lightning_wire_type },
        { key: "optical_wire_type", label: "Оптик трос тури", value: pole.optical_wire_type },
        { key: "insulator_type", label: "Изолятор (тур/сони)", value: pole.insulator_type },
        { key: "fittings", label: "Илиш-улаш арматураси", value: pole.fittings },
        { key: "bird_protection", label: "Қушлардан ҳимоя", value: pole.bird_protection },
        { key: "inspection", label: "Кўрик маълумоти", value: pole.inspection },
      ],
    };
  }, [city, network, pole]);

  if (loading) {
    return <PassportState title="Таянч паспорти юкланмоқда" text="Опора маълумотлари тайёрланмоқда." />;
  }

  if (error) {
    return <PassportState title="Таянч паспорти очилмади" text={error} tone="error" />;
  }

  if (notFound || !network || !pole || !view) {
    return <PassportState title="Таянч топилмади" text="Линия ва таянч URL параметрларини текширинг." tone="error" />;
  }

  const hasCoordinates = displayValue(pole.latitude) !== DASH && displayValue(pole.longitude) !== DASH;

  return (
    <Layout>
      <div className="container">
        <main className="passport-page passport-page--pole">
          <section className="passport-hero passport-hero--pole">
            <div className="passport-breadcrumbs">
              <Link to="/">Бош саҳифа</Link>
              <span>/</span>
              <Link to="/indicators/networks">ЭУҲТ</Link>
              <span>/</span>
              <Link to={`/indicators/networks/${encodeURIComponent(slug)}`}>
                {network.name || slug}
              </Link>
              <span>/</span>
              <span>Таянч №{pole.name}</span>
            </div>

            <div className="passport-hero__grid">
              <div>
                <span className="passport-kicker">Таянч паспорти</span>
                <h1>Таянч №{pole.name}</h1>
                <p>
                  {network.name || DASH} линияси, {city?.name || DASH} филиали.
                </p>
              </div>

              <aside className="passport-identity">
                <span className="passport-identity__label">GPS</span>
                <strong>{hasCoordinates ? "OK" : DASH}</strong>
                <span>{network.voltage ? `${network.voltage} кВ` : "Кучланиш йўқ"}</span>
              </aside>
            </div>
          </section>

          <section className="passport-metrics">
            <div className="passport-metric">
              <span>Линия</span>
              <strong>{network.name || DASH}</strong>
            </div>
            <div className="passport-metric">
              <span>Филиал</span>
              <strong>{city?.name || DASH}</strong>
            </div>
            <div className="passport-metric">
              <span>Кўриклар</span>
              <strong>{view.inspections.length}</strong>
            </div>
            <div className="passport-metric">
              <span>Охирги кўрик</span>
              <strong>{formatDate(view.latestInspection?.date || view.latestInspection?.date_created)}</strong>
            </div>
          </section>

          <div className="passport-layout">
            <div className="passport-main">
              <section className="passport-card">
                <div className="passport-card__head">
                  <span className="passport-section-number">01</span>
                  <div>
                    <h2>Техник маълумотлар</h2>
                    <p>Таянчнинг асосий конструктив ва линияга боғланиш маълумотлари.</p>
                  </div>
                </div>
                <FieldGrid fields={view.fields} />
              </section>

              <section className="passport-card">
                <div className="passport-card__head">
                  <span className="passport-section-number">02</span>
                  <div>
                    <h2>Координаталар</h2>
                    <p>Таянчнинг GPS маълумотлари.</p>
                  </div>
                </div>

                <div className="passport-coordinate-panel">
                  <div>
                    <span>Кенглик</span>
                    <strong>{displayValue(pole.latitude)}</strong>
                  </div>
                  <div>
                    <span>Узунлик</span>
                    <strong>{displayValue(pole.longitude)}</strong>
                  </div>
                </div>
              </section>

              <section className="passport-card">
                <div className="passport-card__head">
                  <span className="passport-section-number">03</span>
                  <div>
                    <h2>Кўриклар тарихи</h2>
                    <p>Опора бўйича мавжуд эксплуатация ёзувлари.</p>
                  </div>
                </div>
                <InspectionTimeline inspections={view.inspections} />
              </section>
            </div>

            <aside className="passport-side">
              <section className="passport-card passport-card--sticky">
                <div className="passport-card__head passport-card__head--compact">
                  <span className="passport-section-number">04</span>
                  <div>
                    <h2>Охирги ҳолат</h2>
                    <p>Сўнгги кўрик бўйича қисқа маълумот.</p>
                  </div>
                </div>

                {view.latestInspection ? (
                  <div className="passport-latest">
                    <span className="passport-chip passport-chip--maintenance">
                      {formatDate(view.latestInspection.date || view.latestInspection.date_created)}
                    </span>
                    <p>{view.latestInspection.comment || "Изоҳ киритилмаган."}</p>
                  </div>
                ) : (
                  <div className="passport-empty">Кўриклар киритилмаган.</div>
                )}
              </section>

              <section className="passport-card">
                <div className="passport-card__head passport-card__head--compact">
                  <span className="passport-section-number">F</span>
                  <div>
                    <h2>Фото ва файллар</h2>
                    <p>Опорага боғланган медиа маълумотлар.</p>
                  </div>
                </div>
                <FileGrid files={view.files} />
              </section>
            </aside>
          </div>
        </main>
      </div>
    </Layout>
  );
};

export default Poles;
